import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  Copy,
  Loader2,
  Plug,
  RotateCcw,
  Sparkles,
  Square,
  SquarePen,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAssistant } from "./assistant-context";
import AssistantMarkdown from "./AssistantMarkdown";
import { buildAssistantContext, getSuggestedPrompts } from "../../lib/assistant-links";
import { describeAssistantScope, type AssistantScopeCopy } from "../../lib/assistant-access";
import {
  ASSISTANT_MAX_MESSAGE_LENGTH,
  describeAssistantError,
  describeStopReason,
  toolActivityLabel,
  type ChatMessage,
  type ToolActivity,
} from "../../lib/assistant-messages";

const SMALL_SCREEN_QUERY = "(max-width: 639px)";
const STICK_THRESHOLD_PX = 80;
const COMPOSER_MAX_HEIGHT_PX = 200;

function isSmallScreen(): boolean {
  return typeof window !== "undefined" && window.matchMedia(SMALL_SCREEN_QUERY).matches;
}

function ToolChip({ tool }: { tool: ToolActivity }) {
  const label = toolActivityLabel(tool);
  return (
    <span
      className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground"
      title={tool.summary ? `${label} · ${tool.summary}` : label}
    >
      {tool.status === "running" && (
        <Loader2 aria-hidden className="h-3 w-3 shrink-0 animate-spin" />
      )}
      {tool.status === "ok" && (
        <Check aria-hidden className="h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400" />
      )}
      {tool.status === "error" && (
        <X aria-hidden className="h-3 w-3 shrink-0 text-red-600 dark:text-red-400" />
      )}
      <span className="shrink-0 text-foreground/80">{label}</span>
      {tool.summary && (
        <span className="truncate" dir="auto">
          · {tool.summary}
        </span>
      )}
      <span className="sr-only">
        {tool.status === "running" ? "in progress" : tool.status === "ok" ? "succeeded" : "failed"}
      </span>
    </span>
  );
}

function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground" role="status">
      <span className="flex gap-1" aria-hidden>
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce [animation-delay:-0.3s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce [animation-delay:-0.15s]" />
        <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/70 animate-bounce" />
      </span>
      <span>Thinking…</span>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      aria-label={copied ? "Copied" : "Copy message"}
      title={copied ? "Copied" : "Copy message"}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
      ) : (
        <Copy className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

function ErrorNotice({
  message,
  isLast,
  onRetry,
  onNewChat,
}: {
  message: ChatMessage;
  isLast: boolean;
  onRetry: () => void;
  onNewChat: () => void;
}) {
  const copy = describeAssistantError(message.error);
  return (
    <div
      role="alert"
      className="mt-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm dark:border-red-500/30 dark:bg-red-500/10"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-red-700 dark:text-red-300">{copy.title}</p>
          <p className="mt-0.5 text-red-700/90 dark:text-red-200/80" dir="auto">
            {copy.description}
          </p>
          {isLast && (copy.canRetry || copy.suggestNewChat) && (
            <div className="mt-2 flex flex-wrap gap-2">
              {copy.canRetry && (
                <Button size="sm" variant="outline" onClick={onRetry}>
                  <RotateCcw />
                  Retry
                </Button>
              )}
              {copy.suggestNewChat && (
                <Button size="sm" variant="outline" onClick={onNewChat}>
                  <SquarePen />
                  New chat
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MessageItem({
  message,
  isLast,
  onRetry,
  onNewChat,
  onInternalNavigate,
}: {
  message: ChatMessage;
  isLast: boolean;
  onRetry: () => void;
  onNewChat: () => void;
  onInternalNavigate: () => void;
}) {
  if (message.role === "user") {
    return (
      <div className="flex justify-end">
        <div
          className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-muted px-3.5 py-2 text-sm text-foreground"
          dir="auto"
        >
          {message.text}
        </div>
      </div>
    );
  }

  const streaming = message.status === "streaming";
  const hasRunningTool = message.tools.some((tool) => tool.status === "running");
  const showThinking = streaming && !hasRunningTool && (!message.text || message.pendingBreak);
  const showCursor = streaming && !!message.text && !message.pendingBreak && !hasRunningTool;

  return (
    <div className="group flex gap-3">
      <div
        aria-hidden
        className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-border bg-card text-muted-foreground"
      >
        <Sparkles className="h-3.5 w-3.5" />
      </div>
      <div className="min-w-0 flex-1">
        {message.tools.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-1.5">
            {message.tools.map((tool) => (
              <ToolChip key={tool.id} tool={tool} />
            ))}
          </div>
        )}
        {message.text && (
          <AssistantMarkdown text={message.text} onInternalNavigate={onInternalNavigate} />
        )}
        {showCursor && (
          <span
            aria-hidden
            className="ms-0.5 inline-block h-4 w-1.5 translate-y-0.5 animate-pulse rounded-sm bg-foreground/70"
          />
        )}
        {showThinking && <ThinkingIndicator />}
        {message.notice && (
          <p className="mt-2 text-xs text-muted-foreground">{message.notice}</p>
        )}
        {message.status === "done" && describeStopReason(message.stopReason) && (
          <p className="mt-2 text-xs text-muted-foreground">
            {describeStopReason(message.stopReason)}
          </p>
        )}
        {message.status === "stopped" && (
          <p className="mt-1 text-xs text-muted-foreground">Stopped</p>
        )}
        {message.status === "error" && (
          <ErrorNotice
            message={message}
            isLast={isLast}
            onRetry={onRetry}
            onNewChat={onNewChat}
          />
        )}
        {!streaming && message.text && (
          <div className="mt-1 flex opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:focus-within:opacity-100">
            <CopyButton text={message.text} />
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  scope,
  prompts,
  disabled,
  onPick,
}: {
  scope: AssistantScopeCopy;
  prompts: string[];
  disabled: boolean;
  onPick: (prompt: string) => void;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center px-2 py-10 text-center">
      <div className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-card">
        <Sparkles aria-hidden className="h-5 w-5 text-foreground" />
      </div>
      <h3 className="mt-4 text-base font-semibold text-foreground">{scope.title}</h3>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{scope.description}</p>
      <div className="mt-6 flex w-full max-w-md flex-col gap-2">
        {prompts.map((prompt) => (
          <button
            key={prompt}
            type="button"
            disabled={disabled}
            onClick={() => onPick(prompt)}
            className="rounded-lg border border-border bg-card px-3 py-2 text-start text-sm text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  );
}

function AvailabilityNotice() {
  const { statusState, enabled, refreshStatus } = useAssistant();
  if (enabled) return null;
  if (statusState === "loading" || statusState === "idle") {
    return (
      <div className="flex items-center gap-2 border-b border-border px-4 py-2 text-xs text-muted-foreground">
        <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
        Checking assistant availability…
      </div>
    );
  }
  if (statusState === "forbidden") {
    return (
      <div className="border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
        The assistant isn't available for your role yet. Ask an admin if you need access.
      </div>
    );
  }
  if (statusState === "error") {
    return (
      <div className="flex items-center justify-between gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
        <span>Couldn't reach the assistant service.</span>
        <Button size="sm" variant="ghost" className="h-7" onClick={refreshStatus}>
          Retry
        </Button>
      </div>
    );
  }
  return (
    <div className="border-b border-border bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
      The assistant isn't configured on the server yet. Ask an admin to set the Anthropic API key.
    </div>
  );
}

export default function AssistantPanel() {
  const {
    available,
    capabilities,
    enabled,
    model,
    open,
    setOpen,
    messages,
    isStreaming,
    historyLoading,
    historyError,
    reloadHistory,
    draft,
    setDraft,
    draftVersion,
    send,
    stop,
    retry,
    newChat,
  } = useAssistant();
  const location = useLocation();
  const [skipEnterAnimation, setSkipEnterAnimation] = useState(open);
  const [isPinnedToBottom, setIsPinnedToBottom] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const pinnedRef = useRef(true);

  const context = useMemo(() => buildAssistantContext(location.pathname), [location.pathname]);
  const prompts = useMemo(
    () => getSuggestedPrompts(location.pathname, capabilities),
    [location.pathname, capabilities],
  );
  const scope = useMemo(() => describeAssistantScope(capabilities), [capabilities]);

  useEffect(() => {
    if (!open) setSkipEnterAnimation(false);
  }, [open]);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "auto") => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  useLayoutEffect(() => {
    if (pinnedRef.current) scrollToBottom();
  }, [messages, historyLoading, scrollToBottom]);

  useEffect(() => {
    if (!open) return;
    pinnedRef.current = true;
    setIsPinnedToBottom(true);
    const frame = window.requestAnimationFrame(() => scrollToBottom());
    return () => window.cancelAnimationFrame(frame);
  }, [open, scrollToBottom]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const pinned = distance < STICK_THRESHOLD_PX;
    pinnedRef.current = pinned;
    setIsPinnedToBottom(pinned);
  };

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`;
  }, [draft, open]);

  useEffect(() => {
    if (!open || draftVersion === 0) return;
    const frame = window.requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [draftVersion, open]);

  const closeOnSmallScreens = useCallback(() => {
    if (isSmallScreen()) setOpen(false);
  }, [setOpen]);

  const trimmedLength = draft.trim().length;
  const overLimit = draft.length > ASSISTANT_MAX_MESSAGE_LENGTH;
  const composerDisabled = !enabled || historyLoading;
  const canSend = !composerDisabled && !isStreaming && trimmedLength > 0 && !overLimit;

  const submit = () => {
    if (!canSend) return;
    pinnedRef.current = true;
    setIsPinnedToBottom(true);
    send(draft, context);
    setDraft("");
  };

  const pickPrompt = (prompt: string) => {
    if (!enabled || isStreaming) return;
    pinnedRef.current = true;
    send(prompt, context);
  };

  const onComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  const startNewChat = () => {
    newChat();
    window.requestAnimationFrame(() => textareaRef.current?.focus());
  };

  if (!available) return null;

  const lastIndex = messages.length - 1;
  const showEmpty = messages.length === 0 && !historyLoading && !historyError;

  return (
    <Sheet open={open} onOpenChange={setOpen} modal={false}>
      <SheetContent
        side="right"
        aria-describedby="assistant-panel-description"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          textareaRef.current?.focus();
        }}
        onInteractOutside={(event) => event.preventDefault()}
        className={cn(
          "flex w-full flex-col gap-0 p-0 shadow-2xl sm:max-w-xl",
          skipEnterAnimation && "data-[state=open]:animate-none",
        )}
      >
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border pl-4 pr-12">
          <Sparkles aria-hidden className="h-4 w-4 text-foreground" />
          <SheetTitle className="text-[15px] font-semibold">Ask Claude</SheetTitle>
          {model && (
            <span className="hidden truncate rounded-md border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground sm:inline">
              {model}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={startNewChat}
              disabled={messages.length === 0 && !isStreaming && !historyError}
              aria-label="New chat"
              title="New chat"
            >
              <SquarePen />
            </Button>
            <Link
              to="/settings/mcp"
              onClick={closeOnSmallScreens}
              aria-label="MCP access settings for Claude Desktop and Claude Code"
              title="Use these tools from Claude Desktop or Claude Code"
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Plug className="h-4 w-4" />
            </Link>
          </div>
        </div>
        <SheetDescription id="assistant-panel-description" className="sr-only">
          {scope.panelDescription}
        </SheetDescription>

        <AvailabilityNotice />

        <div className="relative min-h-0 flex-1">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="h-full overflow-y-auto px-4 py-4"
            aria-live="polite"
            aria-busy={isStreaming}
            aria-label="Conversation"
          >
            {historyLoading && (
              <div className="space-y-4" aria-label="Loading conversation">
                <Skeleton className="ml-auto h-9 w-2/3 rounded-2xl" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            )}

            {historyError && messages.length === 0 && (
              <div
                role="alert"
                className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
              >
                <p>Couldn't load your previous conversation.</p>
                <div className="mt-2 flex gap-2">
                  <Button size="sm" variant="outline" onClick={reloadHistory}>
                    <RotateCcw />
                    Retry
                  </Button>
                  <Button size="sm" variant="outline" onClick={startNewChat}>
                    <SquarePen />
                    New chat
                  </Button>
                </div>
              </div>
            )}

            {showEmpty && (
              <EmptyState
                scope={scope}
                prompts={prompts}
                disabled={!enabled || isStreaming}
                onPick={pickPrompt}
              />
            )}

            {messages.length > 0 && (
              <div className="space-y-5">
                {messages.map((message, index) => (
                  <MessageItem
                    key={message.id}
                    message={message}
                    isLast={index === lastIndex}
                    onRetry={retry}
                    onNewChat={startNewChat}
                    onInternalNavigate={closeOnSmallScreens}
                  />
                ))}
              </div>
            )}
          </div>

          {!isPinnedToBottom && messages.length > 0 && (
            <button
              type="button"
              onClick={() => {
                pinnedRef.current = true;
                setIsPinnedToBottom(true);
                scrollToBottom("smooth");
              }}
              aria-label="Scroll to latest message"
              className="absolute bottom-3 left-1/2 inline-flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md transition-colors hover:bg-accent"
            >
              <ArrowDown className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="shrink-0 border-t border-border bg-background p-3">
          <div
            className={cn(
              "rounded-xl border border-input bg-card px-3 pb-2 pt-2.5 transition-colors focus-within:border-ring",
              composerDisabled && "opacity-60",
            )}
          >
            <label htmlFor="assistant-composer" className="sr-only">
              Message Claude
            </label>
            <textarea
              id="assistant-composer"
              ref={textareaRef}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onComposerKeyDown}
              disabled={composerDisabled}
              rows={1}
              dir="auto"
              placeholder={
                enabled ? "Ask about orders, customers, shipments…" : "Assistant unavailable"
              }
              className="block max-h-[200px] min-h-[24px] w-full resize-none bg-transparent text-sm leading-6 text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed"
              aria-describedby="assistant-composer-hint"
            />
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <span id="assistant-composer-hint" className="truncate text-[11px] text-muted-foreground">
                Enter to send · Shift+Enter for a new line
              </span>
              <div className="flex shrink-0 items-center gap-2">
                <span
                  className={cn(
                    "text-[11px] tabular-nums",
                    overLimit ? "font-medium text-red-600 dark:text-red-400" : "text-muted-foreground",
                  )}
                  aria-label={`${draft.length} of ${ASSISTANT_MAX_MESSAGE_LENGTH} characters`}
                >
                  {draft.length.toLocaleString()}/{ASSISTANT_MAX_MESSAGE_LENGTH.toLocaleString()}
                </span>
                {isStreaming ? (
                  <Button
                    size="icon"
                    variant="secondary"
                    className="h-8 w-8 rounded-full"
                    onClick={stop}
                    aria-label="Stop generating"
                    title="Stop generating"
                  >
                    <Square className="!size-3 fill-current" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    className="h-8 w-8 rounded-full"
                    onClick={submit}
                    disabled={!canSend}
                    aria-label="Send message"
                    title="Send message"
                  >
                    <ArrowUp />
                  </Button>
                )}
              </div>
            </div>
          </div>
          <p className="mt-2 px-1 text-[11px] text-muted-foreground">
            {scope.footer}
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
